<!-- Cart Trigger Button -->
<div class="cart-wrapper">
  <button #cartBtn class="cart-trigger" (click)="toggleCart()" aria-label="Open cart">
    <span class="material-symbols-outlined"> shopping_cart </span>
    <span class="cart-badge" *ngIf="cartSummary().totalQuantity > 0">{{ cartSummary().totalQuantity }}</span>
  </button>

  <!-- Dropdown -->
  <div #dropdown class="cart-dropdown">
    <!-- Arrow -->
    <div class="dropdown-arrow"></div>

    <!-- Header -->
    <div class="dropdown-header">
      <span class="dropdown-title">Cart</span>
      @if (totalItems() > 0) {
        <span class="item-count-pill">
          {{ totalItems() }}
          @if (totalItems() === 1) {
            {{ 'summary.item' | translate: { fgbdefault: 'item' } }}
          } @else {
            {{ 'summary.items' | translate: { fgbdefault: 'items' } }}
          }
        </span>
      }
    </div>

    <!-- Items -->
    <div class="dropdown-body">
      <ul #cartList class="cart-items-list" *ngIf="cartSummary().totalQuantity > 0; else emptyState">
        <ng-container *ngTemplateOutlet="cartItemTemplate; context: { items: cartItems() }"></ng-container>
      </ul>
      <ng-template #emptyState>
        <div class="empty-state">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.3"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 01-8 0" />
          </svg>
          <p>Your cart is empty</p>
        </div>
      </ng-template>
    </div>

    <!-- Footer -->
    <div class="dropdown-footer" *ngIf="cartItems().length > 0">
      <div class="summary__total">
        <p class="summary__total-label">{{ 'summary.total' | translate: { fgbdefault: 'Total' } }}</p>
        <p class="summary__total-value">
          @if (isFreeBasket()) {
            {{ 'fgbcl.currency.free' | translate }}
          } @else {
            @if (hasPoints()) {
              {{ totalPoints() | number }} {{ 'fgbcl.currency.pts' | translate }}
            }
            @if (hasPoints() && hasMoneyTotal()) {
              +
            }
            @if (hasMoneyTotal() || !hasPoints()) {
              {{ totalPrice() / 100 | FortressCurrencyPipe }}
            }
          }
        </p>
      </div>
      @if (isDisabled()) {
        <div class="error-message">
          Some items in your cart are no longer available for purchase. Please remove them to proceed to checkout.
        </div>
      }
      <button class="checkout-btn btn-rounded" (click)="proceedToCheckout()" [disabled]="isDisabled()">
        Proceed to Checkout
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </button>
    </div>
  </div>
</div>

<ng-template #cartItemTemplate let-items="items">
  <li class="summary-item" *ngFor="let item of items">
    <div class="summary-item__image-wrapper">
      <img
        class="summary-item__image"
        [src]="item.productData.ImageURL ?? item.productData.PrimaryImage | contentImage: '6'"
        [alt]="item.productData.DisplayName"
      />
      <span class="summary-item__quantity">{{ item.purchaseQuantity }}</span>
    </div>

    <div class="summary-item__body">
      <div class="summary-item__info">
        <p class="summary-item__name">{{ item.productData.DisplayName }}</p>

        @switch (item.productData.Status) {
          @case (marketplaceStatus.ViewableOnly) {
            <p class="summary-item__status">
              <span class="icon material-icons">lock</span>
              {{ 'reward.status.locked' | translate }}
            </p>
          }
          @case (marketplaceStatus.SoldOut) {
            <p class="summary-item__status">{{ 'reward.status.sold.out' | translate }}</p>
          }
          @case (marketplaceStatus.LimitReached) {
            <p class="summary-item__status">{{ 'reward.status.limit.reached' | translate }}</p>
          }
          @case (marketplaceStatus.NotEnoughPoints) {
            <p class="summary-item__status">{{ 'reward.status.not.enough.points' | translate }}</p>
          }
          @default {
            <p class="summary-item__source">
              @switch (cartTypeOf(item)) {
                @case (itemCartType.MARKETPLACE) {
                  {{ 'cart.type.marketplace' | translate: { fgbdefault: 'Marketplace' } }}
                }
                @case (itemCartType.PRODUCT) {
                  {{ 'cart.type.product' | translate: { fgbdefault: 'Product' } }}
                }
              }
            </p>
          }
        }
      </div>

      @if (!isUnavailable(item)) {
        <p class="summary-item__price">
          @switch (priceKindOf(item)) {
            @case (summaryPriceKind.POINTS) {
              <span class="summary-item__price-points">
                {{ item.purchaseQuantity * item.productData.PointValue | number: '1.0-0' }}
                <span class="summary-item__price-unit">{{ 'fgbcl.currency.pts' | translate }}</span>
              </span>
            }
            @case (summaryPriceKind.ECASH) {
              {{ (item.purchaseQuantity * item.productData.EcashValue) / 100 | FortressCurrencyPipe }}
            }
            @case (summaryPriceKind.FREE) {
              {{ 'fgbcl.currency.free' | translate }}
            }
            @default {
              {{ merchantTotalOf(item) / 100 | FortressCurrencyPipe }}
            }
          }
        </p>
      }
    </div>

    <button class="remove-btn" (click)="removeItem(item.id)" aria-label="Remove item">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  </li>
</ng-template>

<!-- Click outside overlay -->
<div class="click-outside" *ngIf="isOpen" (click)="closeCart()"></div>
